import type {
  ProposalDocumentDto,
  TreeScheduleRow,
} from '@lib/dto/proposal-document.dto'
import { parseMoney } from '@lib/helpers/money'
import {
  irrigationScheduleBlocks,
  priceScheduleRow,
  ratePerHourCents,
} from '@lib/helpers/proposal-selection'
import type { PricedTree } from './treeWork'

/** One kind of work in the tray: how many trees, how long, how much. */
export interface WorkLine {
  trees: number
  minutes: number
  cents: number
}

/** What a site's next visit comes to, as the tray breaks it down. */
export interface TreeTally {
  /** What counts toward the bid's headline, before tax. */
  countedCents: number
  pruning: WorkLine
  removals: WorkLine
  treatments: WorkLine
  /** Crew minutes for the counted work. */
  minutes: number
  /** Trees in the next visit with no time yet: the headline's "+". */
  unestimated: number
  /** Work marked optional, left out of the headline. */
  optional: { trees: number; cents: number }
  /** Removals quoted beside pruning, which the client may choose instead. */
  removalAlternatives: { trees: number; cents: number }
}

const EMPTY_LINE: WorkLine = { trees: 0, minutes: 0, cents: 0 }

function add(line: WorkLine, minutes: number, cents: number): WorkLine {
  return {
    trees: line.trees + 1,
    minutes: line.minutes + minutes,
    cents: line.cents + cents,
  }
}

/** Whether a row has pruning to price: jobs, a time, or a price by hand. */
function hasPruningWork(row: TreeScheduleRow): boolean {
  return (
    row.noPruningRecommended !== true &&
    (row.jobs.length > 0 ||
      (row.estimatedLaborMinutes ?? 0) > 0 ||
      (row.pruningPriceOverride ?? null) !== null)
  )
}

/**
 * Whether the bid leads with "Everything recommended", leaving optional work
 * out of its first figure.
 *
 * The rule `planFigures` applies to the whole proposal: only when some work
 * is optional and some is not. A bid that is all optional leads with all of
 * it, since leaving everything out would open the proposal at nothing.
 * Repairs are never optional and count as recommended work.
 */
export function leadsWithRecommended(
  rows: readonly Pick<TreeScheduleRow, 'isOptional'>[],
  repairCount: number,
): boolean {
  const optional = rows.filter((row) => row.isOptional).length
  return optional > 0 && rows.length - optional + repairCount > 0
}

/**
 * Break a site's next visit down the way the tray shows it.
 *
 * Each tree is priced as the client's page first opens it (pruned where
 * pruning is quoted, removed where only removal is), so `countedCents` is
 * that page's figure for the site.
 */
export function tallyTrees(
  trees: readonly PricedTree[],
  options: { countOptional: boolean },
): TreeTally {
  let pruning = EMPTY_LINE
  let removals = EMPTY_LINE
  let treatments = EMPTY_LINE
  let countedCents = 0
  let unestimated = 0
  const optional = { trees: 0, cents: 0 }
  const removalAlternatives = { trees: 0, cents: 0 }

  for (const tree of trees) {
    const { row, cents } = tree
    if (row.estimatedLaborMinutes === null) unestimated += 1
    if (row.isOptional && !options.countOptional) {
      if (cents.totalCents > 0) {
        optional.trees += 1
        optional.cents += cents.totalCents
      }
      continue
    }
    countedCents += cents.totalCents
    if (cents.choice === 'REMOVE') {
      removals = add(removals, row.removal?.minutes ?? 0, cents.removalCents)
      continue
    }
    if (hasPruningWork(row)) {
      pruning = add(pruning, row.estimatedLaborMinutes ?? 0, cents.pruningCents)
    }
    if (cents.problemCents > 0 || tree.treatmentMinutes > 0) {
      treatments = add(treatments, tree.treatmentMinutes, cents.problemCents)
    }
    if (row.removal !== null) {
      removalAlternatives.trees += 1
      removalAlternatives.cents += parseMoney(row.removal.amount) ?? 0
    }
  }

  return {
    countedCents,
    pruning,
    removals,
    treatments,
    minutes: pruning.minutes + removals.minutes + treatments.minutes,
    unestimated,
    optional,
    removalAlternatives,
  }
}

/** One site of a proposal's draft, as its document prices it. */
export interface DocumentSiteTrees {
  siteName: string
  trees: PricedTree[]
}

/**
 * Every site's next-visit trees in a draft's document, priced as offered.
 *
 * Used for the bid's other sites, which this screen is not editing; the site
 * being surveyed is priced from the phone instead, so it moves as it changes.
 */
export function documentSiteTrees(
  document: ProposalDocumentDto,
): DocumentSiteTrees[] {
  const centsPerHour = ratePerHourCents(document)
  const sites: DocumentSiteTrees[] = []
  for (const block of document.blocks) {
    if (block.type !== 'treeSchedule') continue
    sites.push({
      siteName: block.siteName,
      trees: block.rows.map((row) => ({
        treeId: row.treeId ?? row.label,
        row,
        cents: priceScheduleRow(row, centsPerHour),
        treatmentMinutes: 0,
      })),
    })
  }
  return sites
}

/** How many repairs a draft's document bids, across its sites. */
export function documentRepairCount(document: ProposalDocumentDto): number {
  return irrigationScheduleBlocks(document).reduce(
    (sum, block) => sum + block.repairs.length,
    0,
  )
}
